// ============================================================================
// SAMPLE CATEGORY SERVICE (Reference Implementation)
// ============================================================================
// Business logic and API calls for Category module

import { get, post, put, del } from '../client/axiosClient';
import { sampleCategoryEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  SampleCategory,
  CreateSampleCategoryRequest,
  UpdateSampleCategoryRequest,
  SampleCategoryListItem,
  SampleCategoryFilterParams,
  SampleCategoryWithServices,
} from '../types';

/**
 * Category Service Class
 */
class SampleCategoryService {
  /**
   * Get all categories
   */
  async getAll(params?: SampleCategoryFilterParams): Promise<ListResponse<SampleCategoryListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(sampleCategoryEndpoints.list(), params)
      : sampleCategoryEndpoints.list();

    const response = await get<ListResponse<SampleCategoryListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get category by ID
   */
  async getById(id: ID): Promise<ApiResponse<SampleCategory>> {
    const response = await get<ApiResponse<SampleCategory>>(sampleCategoryEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new category
   */
  async create(data: CreateSampleCategoryRequest): Promise<ApiResponse<SampleCategory>> {
    const response = await post<ApiResponse<SampleCategory>>(
      sampleCategoryEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update category
   */
  async update(id: ID, data: UpdateSampleCategoryRequest): Promise<ApiResponse<SampleCategory>> {
    const response = await put<ApiResponse<SampleCategory>>(
      sampleCategoryEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete category
   */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(sampleCategoryEndpoints.delete(id));
    return response.data;
  }

  /**
   * Search categories
   */
  async search(params: SampleCategoryFilterParams): Promise<ListResponse<SampleCategoryListItem>> {
    const response = await post<ListResponse<SampleCategoryListItem>>(
      sampleCategoryEndpoints.search(),
      params
    );
    return response.data;
  }

  /**
   * Get category with services
   */
  async getWithServices(id: ID): Promise<ApiResponse<SampleCategoryWithServices>> {
    const response = await get<ApiResponse<SampleCategoryWithServices>>(
      sampleCategoryEndpoints.withServices(id)
    );
    return response.data;
  }

  /**
   * Get active categories
   */
  async getActive(): Promise<ListResponse<SampleCategoryListItem>> {
    const response = await get<ListResponse<SampleCategoryListItem>>(
      sampleCategoryEndpoints.active()
    );
    return response.data;
  }

  /**
   * Bulk delete categories
   */
  async bulkDelete(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(sampleCategoryEndpoints.bulkDelete(), {
      data: { ids },
    });
    return response.data;
  }

  /**
   * Get categories for dropdown/select
   */
  async getForDropdown(): Promise<SampleCategoryListItem[]> {
    const response = await this.getActive();
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const sampleCategoryService = new SampleCategoryService();
export default sampleCategoryService;
